import { useRef, useState, type ReactNode } from "react";
import { enviarArquivos, type ProgressoUpload } from "../../lib/api.js";
import { reduzirImagem } from "../../lib/imagem.js";
import { Pagina } from "../../components/Pagina.js";
import { Botao, Falha, Girando, Selo, Vazio, cx } from "../../components/ui.js";

export interface ItemAcervo {
  id: number;
  url: string;
  label?: string | null;
  originalName: string;
  mimeType: string;
}

/**
 * Grade de acervo com envio em massa.
 *
 * Uma implementação para banco de imagens, capas e divulgações. No sistema
 * anterior eram três páginas com o mesmo molde duplicado — `ImageBankPage` e
 * `BookCoversPage` divergiam em 85 de 763 linhas.
 */
export function PaginaAcervo({
  titulo,
  descricao,
  rotaUpload,
  itens,
  carregando,
  proporcao,
  aceita,
  aoRecarregar,
  aoExcluir,
  excluindo,
  editor,
}: {
  titulo: string;
  descricao: string;
  rotaUpload: string;
  itens: ItemAcervo[];
  carregando: boolean;
  proporcao: "square" | "cover";
  aceita: string;
  aoRecarregar: () => void;
  aoExcluir: (ids: number[]) => Promise<void>;
  excluindo: boolean;
  /**
   * Editor por item, opcional. Só a tela de divulgações precisa — banco de
   * imagens e capas não têm o que editar além do arquivo em si, e forçar um
   * botão inútil nas três telas seria pior do que a prop extra.
   */
  editor?: (item: ItemAcervo, aoFechar: () => void) => ReactNode;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [progresso, setProgresso] = useState<ProgressoUpload | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  async function enviar(lista: FileList | null) {
    if (!lista || lista.length === 0) return;
    setErro(null);

    try {
      const arquivos = await Promise.all(
        Array.from(lista).map((a) => (a.type.startsWith("image/") ? reduzirImagem(a) : a)),
      );

      setProgresso({ enviados: 0, total: 1, percent: 0 });
      await enviarArquivos(rotaUpload, { nome: "files", arquivos }, setProgresso);
      aoRecarregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível enviar os arquivos");
    } finally {
      setProgresso(null);
    }
  }

  async function excluir() {
    await aoExcluir(selecionados);
    setSelecionados([]);
    setConfirmando(false);
  }

  const enviando = progresso !== null;

  return (
    <Pagina
      titulo={titulo}
      descricao={descricao}
      acao={
        <Botao tom="brasa" carregando={enviando} onClick={() => entrada.current?.click()}>
          Enviar arquivos
        </Botao>
      }
    >
      <input
        ref={entrada}
        type="file"
        accept={aceita}
        multiple
        hidden
        onChange={(e) => {
          void enviar(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="flex flex-col gap-4">
        {enviando && (
          <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-[0.9375rem]">Enviando…</span>
              <span className="num font-mono text-[0.8125rem] text-texto-suave">
                {progresso.percent}%
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-borda">
              <div
                className="h-full rounded-full bg-brasa transition-[width]"
                style={{ width: `${progresso.percent}%` }}
              />
            </div>
          </div>
        )}

        {erro && <Falha mensagem={erro} />}

        {selecionados.length > 0 && (
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-peca border border-brasa/40 bg-brasa-fraca px-4 py-2.5">
            <span className="text-[0.9375rem]">
              <span className="num font-semibold">{selecionados.length}</span> selecionado
              {selecionados.length > 1 ? "s" : ""}
            </span>
            <div className="flex gap-2">
              <Botao tom="fantasma" onClick={() => setSelecionados([])}>
                Limpar
              </Botao>
              <Botao tom="descartar" carregando={excluindo} onClick={() => setConfirmando(true)}>
                Excluir
              </Botao>
            </div>
          </div>
        )}

        {confirmando && (
          <div className="rounded-peca border border-descartar/40 bg-descartar/10 px-4 py-3">
            <p className="text-[0.9375rem]">
              Excluir {selecionados.length} arquivo{selecionados.length > 1 ? "s" : ""}? Isso remove
              do CDN e não tem volta.
            </p>
            <div className="mt-3 flex gap-2">
              <Botao tom="descartar" carregando={excluindo} onClick={() => void excluir()}>
                Excluir mesmo assim
              </Botao>
              <Botao tom="fantasma" onClick={() => setConfirmando(false)}>
                Cancelar
              </Botao>
            </div>
          </div>
        )}

        {carregando ? (
          <div className="flex justify-center py-12">
            <Girando className="size-6 text-texto-fraco" />
          </div>
        ) : itens.length === 0 ? (
          <Vazio
            titulo="Nada aqui ainda"
            descricao={descricao}
            acao={
              <Botao tom="brasa" onClick={() => entrada.current?.click()}>
                Enviar os primeiros
              </Botao>
            }
          />
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {itens.map((item) => {
              const ativo = selecionados.includes(item.id);
              return (
                <li key={item.id}>
                  <button
                    onClick={() =>
                      setSelecionados((s) =>
                        ativo ? s.filter((i) => i !== item.id) : [...s, item.id],
                      )
                    }
                    aria-pressed={ativo}
                    className={cx(
                      "relative w-full overflow-hidden rounded-peca border-2 transition-colors",
                      proporcao === "square" ? "aspect-square" : "aspect-[9/16]",
                      ativo ? "border-brasa" : "border-borda hover:border-borda-forte",
                    )}
                  >
                    {item.mimeType.startsWith("video/") ? (
                      <video src={item.url} className="size-full object-cover" muted playsInline />
                    ) : (
                      <img
                        src={item.url}
                        alt={item.label ?? item.originalName}
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    )}

                    {ativo && (
                      <span className="absolute inset-0 bg-brasa/20" aria-hidden>
                        <span className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full bg-brasa text-fundo">
                          ✓
                        </span>
                      </span>
                    )}

                    <span className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-gradient-to-t from-black/85 to-transparent px-2 pb-1.5 pt-5 text-left">
                      <span className="truncate text-[0.75rem] text-texto">
                        {item.label ?? item.originalName}
                      </span>
                      {item.mimeType.startsWith("video/") && <Selo tom="quieto">vídeo</Selo>}
                    </span>
                  </button>

                  {editor && (
                    <button
                      onClick={() => setEditandoId((id) => (id === item.id ? null : item.id))}
                      className="mt-1 w-full rounded-peca px-2 py-1.5 text-left text-[0.8125rem] text-texto-suave transition-colors hover:bg-superficie hover:text-texto"
                    >
                      {editandoId === item.id ? "Fechar" : "Editar"}
                    </button>
                  )}

                  {editor && editandoId === item.id && (
                    <div className="mt-1 rounded-peca border border-borda bg-superficie-alta p-3">
                      {editor(item, () => setEditandoId(null))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Pagina>
  );
}
